/**
 * Resolves a parsed query against schema data (slugs and names to ids, "me", relative dates).
 * Both evaluators (toSql on the server, toPredicate on the client) consume the resolved form,
 * which is what keeps them in agreement. Pure: safe to import from the browser.
 *
 * Date semantics (days are UTC):
 *   absolute  created:2026-01-01 = that day; >2026-01-01 = from the next day; <= = up to the end of that day
 *   past fields (created, updated, completed) with a duration compare the age:
 *             created:<7d = less than 7 days ago; updated:>30d = more than 30 days ago; created:7d = within 7 days
 *   future fields (due, start) with a duration compare against now + duration:
 *             due:<7d = due before 7 days from now (overdue included); due:7d is the same as due:<=7d
 */
import {
  CLOSED_CATEGORIES,
  type CompareOp,
  type FieldClause,
  type Localized,
  localizedNames,
  parsePriority,
  priorityUrgency,
  type QueryAst,
  type QuerySort,
  type QueryWarning,
  STATUS_CATEGORIES,
  type StatusCategory,
} from "@plannello/shared";
import type { FieldGroupDef, FieldLabelDef } from "../fields.ts";
import { COMPARABLE_FIELDS } from "./parse.ts";

export interface QuerySchema {
  types: { id: string; slug: string; name: Localized }[];
  statuses: { id: string; slug: string; name: Localized; category: StatusCategory }[];
  products: { id: string; key: string; spaceId: string }[];
  spaces: { id: string; key: string; name: string }[];
  /** Label groups (Fields). Their slugs work as query keys: `area:auth`, `phase:none`, `sort:phase`. */
  labelGroups: FieldGroupDef[];
  labels: (FieldLabelDef & { groupSlug: string | null })[];
  milestones: { id: string; productId: string; name: string; version: string | null }[];
  users: { id: string; name: string; email: string | null }[];
}

export interface ResolveContext extends QuerySchema {
  /** Current user for "me" (null for anonymous evaluation: "me" matches nothing). */
  userId: string | null;
  now: Date;
  /** Item key (current or historic, normalized "PLZ-12") to item id. */
  itemIdByKey: (key: string) => string | undefined;
}

export type ScalarColumn =
  | "id"
  | "typeId"
  | "statusId"
  | "productId"
  | "spaceId"
  | "milestoneId"
  | "parentId"
  | "reporterId"
  | "priority"
  | "estimate"
  | "dueDate"
  | "startDate";
export type SetColumn = "assigneeIds" | "labelIds";
export type RangeColumn = "createdAt" | "updatedAt" | "completedAt" | "dueDate" | "startDate";

/** Every condition is two-valued: a NULL column never matches a positive condition. */
export type Condition =
  | { t: "in"; column: ScalarColumn; values: (string | number | null)[]; negate: boolean }
  /** Any of `values` is in the set, or (`empty`) the set is empty. */
  | { t: "set"; column: SetColumn; values: string[]; empty: boolean; negate: boolean }
  /** gte/lt: ISO timestamps for timestamp columns, YYYY-MM-DD for date columns. */
  | { t: "range"; column: RangeColumn; kind: "timestamp" | "date"; gte?: string; lt?: string; negate: boolean }
  | { t: "blocked"; negate: boolean }
  | { t: "hasChildren"; negate: boolean }
  | { t: "text"; text: string; negate: boolean }
  | { t: "or"; conditions: Condition[]; negate: boolean };

/**
 * A sort with its label group resolved (key "group"): `groupIds` for SQL, `positions` (label id to
 * position) for the client comparator. Items without a label of the group sort last.
 */
export type ResolvedSort = QuerySort & { groupIds?: string[]; positions?: Record<string, number> };

export interface ResolvedQuery {
  conditions: Condition[];
  /** Archived items are excluded unless the query says is:archived. */
  archived: "exclude" | "only";
  sort: ResolvedSort[];
  warnings: QueryWarning[];
}

const NOTHING: Condition = { t: "in", column: "id", values: [], negate: false };
const lower = (s: string) => s.toLowerCase();
const nameMatches = (value: string, name: Localized | string) =>
  localizedNames(name).some((n) => lower(n) === lower(value));

function negated(c: Condition): Condition {
  return { ...c, negate: !c.negate };
}

const DAY = 86_400_000;
const DURATION = /^(\d{1,9})([hdwmy])$/i;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const UNIT_MS: Record<string, number> = { h: 3_600_000, d: DAY, w: 7 * DAY, m: 30 * DAY, y: 365 * DAY };

// Dates outside 0001-01-01..9999-12-30 are not representable as plain ISO strings (JS switches to the
// extended "+010000-..." form and throws RangeError beyond +-8.64e15 ms; Postgres has no year 0 and
// rejects the extended form). Such values (`created:999999999d`, `due:0000-01-01`) are "invalid_value".
const MIN_DATE_MS = -62_135_596_800_000; // 0001-01-01T00:00:00Z
const MAX_DATE_MS = Date.UTC(9999, 11, 31) - 1;
const inRange = (ms: number) => Number.isFinite(ms) && ms >= MIN_DATE_MS && ms <= MAX_DATE_MS;

const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const dayStart = (ms: number) => Math.floor(ms / DAY) * DAY;

const RANGE_FIELDS = {
  created: { column: "createdAt", kind: "timestamp", future: false },
  updated: { column: "updatedAt", kind: "timestamp", future: false },
  completed: { column: "completedAt", kind: "timestamp", future: false },
  due: { column: "dueDate", kind: "date", future: true },
  start: { column: "startDate", kind: "date", future: true },
} as const;

const INVERT: Record<CompareOp, CompareOp> = { "<": ">", ">": "<", "<=": ">=", ">=": "<=", "=": ">=" };

function resolveDate(field: keyof typeof RANGE_FIELDS, op: CompareOp, value: string, now: Date): Condition | null {
  const spec = RANGE_FIELDS[field];
  const v = lower(value);
  // [d0, d1) is the interval the value denotes (a whole day or an instant).
  let d0: number;
  let d1: number;
  let effectiveOp = op;
  const duration = DURATION.exec(v);
  if (duration) {
    const ms = Number(duration[1]) * UNIT_MS[lower(duration[2]!)]!;
    const point = now.getTime() + (spec.future ? ms : -ms);
    if (!inRange(point)) return null;
    if (spec.kind === "date") {
      d0 = dayStart(point);
      d1 = d0 + DAY;
    } else {
      d0 = d1 = point;
    }
    effectiveOp = spec.future ? (op === "=" ? "<=" : op) : INVERT[op];
  } else {
    let day: number;
    if (v === "today") day = dayStart(now.getTime());
    else if (v === "yesterday") day = dayStart(now.getTime()) - DAY;
    else if (v === "tomorrow") day = dayStart(now.getTime()) + DAY;
    else if (DATE.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`))) day = Date.parse(`${v}T00:00:00Z`);
    else return null;
    if (!inRange(day)) return null;
    d0 = day;
    d1 = day + DAY;
  }
  const fmt = (ms: number) => (spec.kind === "date" ? isoDay(ms) : new Date(ms).toISOString());
  const base = { t: "range", column: spec.column, kind: spec.kind, negate: false } as const;
  switch (effectiveOp) {
    case "=":
      return { ...base, gte: fmt(d0), lt: fmt(d1) };
    case ">":
      return { ...base, gte: fmt(d1) };
    case ">=":
      return { ...base, gte: fmt(d0) };
    case "<":
      return { ...base, lt: fmt(d0) };
    case "<=":
      return { ...base, lt: fmt(d1) };
  }
}

function resolvePriority(op: CompareOp, value: string): Condition | null {
  const p = parsePriority(value);
  if (p === null) return null;
  if (op === "=") return { t: "in", column: "priority", values: [p], negate: false };
  const u = priorityUrgency(p);
  const cmp: Record<CompareOp, (x: number) => boolean> = {
    "=": (x) => x === u,
    ">": (x) => x > u,
    ">=": (x) => x >= u,
    "<": (x) => x < u,
    "<=": (x) => x <= u,
  };
  return {
    t: "in",
    column: "priority",
    values: [0, 1, 2, 3, 4].filter((s) => cmp[op](priorityUrgency(s))),
    negate: false,
  };
}

function resolveUser(ctx: ResolveContext, value: string): string[] {
  const v = lower(value);
  return ctx.users
    .filter((u) => u.id === value || lower(u.name) === v || (u.email && lower(u.email) === v))
    .map((u) => u.id);
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function resolveItemRef(ctx: ResolveContext, value: string): string | undefined {
  if (UUID.test(value)) return value.toLowerCase();
  return ctx.itemIdByKey(value.toUpperCase());
}

const statusIdsIn = (ctx: ResolveContext, cats: readonly StatusCategory[]) =>
  ctx.statuses.filter((s) => cats.includes(s.category)).map((s) => s.id);
const OPEN_CATEGORIES = STATUS_CATEGORIES.filter((c) => !CLOSED_CATEGORIES.includes(c));

const inIds = (column: ScalarColumn, values: (string | number | null)[]): Condition => ({
  t: "in",
  column,
  values,
  negate: false,
});

/** One value of a field clause. null = could not resolve (the caller warns and matches nothing). */
function resolveValue(ctx: ResolveContext, clause: FieldClause, value: string): Condition | null {
  const v = lower(value);
  switch (clause.field) {
    case "is":
      switch (v) {
        case "open":
          return inIds("statusId", statusIdsIn(ctx, OPEN_CATEGORIES));
        case "closed":
          return inIds("statusId", statusIdsIn(ctx, CLOSED_CATEGORIES));
        case "blocked":
          return { t: "blocked", negate: false };
        case "mine":
          return ctx.userId
            ? { t: "set", column: "assigneeIds", values: [ctx.userId], empty: false, negate: false }
            : NOTHING;
        case "unassigned":
          return { t: "set", column: "assigneeIds", values: [], empty: true, negate: false };
        default:
          return null;
      }
    case "has": {
      const notNull = (column: ScalarColumn) => negated(inIds(column, [null]));
      switch (v) {
        case "parent":
          return notNull("parentId");
        case "children":
          return { t: "hasChildren", negate: false };
        case "assignee":
        case "assignees":
          return { t: "set", column: "assigneeIds", values: [], empty: true, negate: true };
        case "label":
        case "labels":
          return { t: "set", column: "labelIds", values: [], empty: true, negate: true };
        case "milestone":
          return notNull("milestoneId");
        case "due":
          return notNull("dueDate");
        case "start":
          return notNull("startDate");
        case "estimate":
          return notNull("estimate");
        default:
          return null;
      }
    }
    case "assignee":
      if (v === "none") return { t: "set", column: "assigneeIds", values: [], empty: true, negate: false };
      if (v === "me")
        return ctx.userId
          ? { t: "set", column: "assigneeIds", values: [ctx.userId], empty: false, negate: false }
          : NOTHING;
      return orNull(resolveUser(ctx, value), (ids) => ({
        t: "set",
        column: "assigneeIds",
        values: ids,
        empty: false,
        negate: false,
      }));
    case "reporter":
      if (v === "none") return inIds("reporterId", [null]);
      if (v === "me") return ctx.userId ? inIds("reporterId", [ctx.userId]) : NOTHING;
      return orNull(resolveUser(ctx, value), (ids) => inIds("reporterId", ids));
    case "type":
      return orNull(
        ctx.types.filter((t) => lower(t.slug) === v || nameMatches(value, t.name)).map((t) => t.id),
        (ids) => inIds("typeId", ids),
      );
    case "status": {
      const exact = ctx.statuses.filter((s) => lower(s.slug) === v || nameMatches(value, s.name)).map((s) => s.id);
      if (exact.length > 0) return inIds("statusId", exact);
      if ((STATUS_CATEGORIES as readonly string[]).includes(v))
        return inIds("statusId", statusIdsIn(ctx, [v as StatusCategory]));
      if (v === "open") return inIds("statusId", statusIdsIn(ctx, OPEN_CATEGORIES));
      if (v === "closed") return inIds("statusId", statusIdsIn(ctx, CLOSED_CATEGORIES));
      return null;
    }
    case "priority":
      return resolvePriority(clause.op, value);
    case "product":
      return orNull(
        ctx.products.filter((p) => lower(p.key) === v).map((p) => p.id),
        (ids) => inIds("productId", ids),
      );
    case "space":
      return orNull(
        ctx.spaces.filter((s) => lower(s.key) === v || lower(s.name) === v).map((s) => s.id),
        (ids) => inIds("spaceId", ids),
      );
    case "label": {
      if (v === "none") return { t: "set", column: "labelIds", values: [], empty: true, negate: false };
      const slash = v.indexOf("/");
      const ids =
        slash >= 0
          ? ctx.labels
              .filter((l) => {
                const group = v.slice(0, slash);
                const slug = v.slice(slash + 1);
                return l.groupSlug !== null && lower(l.groupSlug) === group && (slug === "*" || lower(l.slug) === slug);
              })
              .map((l) => l.id)
          : ctx.labels.filter((l) => lower(l.slug) === v || nameMatches(value, l.name)).map((l) => l.id);
      return orNull(ids, (found) => ({ t: "set", column: "labelIds", values: found, empty: false, negate: false }));
    }
    case "parent": {
      if (v === "none") return inIds("parentId", [null]);
      const id = resolveItemRef(ctx, value);
      return id ? inIds("parentId", [id]) : null;
    }
    case "milestone":
      if (v === "none") return inIds("milestoneId", [null]);
      return orNull(
        ctx.milestones
          .filter((m) => lower(m.name) === v || (m.version !== null && lower(m.version) === v))
          .map((m) => m.id),
        (ids) => inIds("milestoneId", ids),
      );
    case "key": {
      const id = resolveItemRef(ctx, value);
      return id ? inIds("id", [id]) : null;
    }
    case "created":
    case "updated":
    case "completed":
    case "due":
    case "start":
      return resolveDate(clause.field, clause.op, value, ctx.now);
  }
}

const groupsBySlug = (ctx: ResolveContext, slug: string) =>
  ctx.labelGroups.filter((g) => !g.archivedAt && lower(g.slug) === lower(slug));

/**
 * One value of a Field clause (`area:auth,checkout`, `area:none`, `area:*`). Built-in keys never
 * reach this: the parser only produces a fieldRef for keys that are not built in.
 */
function resolveFieldValue(ctx: ResolveContext, groups: FieldGroupDef[], value: string): Condition | null {
  const v = lower(value);
  const groupLabels = ctx.labels.filter((l) => l.groupId !== null && groups.some((g) => g.id === l.groupId));
  const set = (ids: string[], negate: boolean): Condition => ({
    t: "set",
    column: "labelIds",
    values: ids,
    empty: false,
    negate,
  });
  if (v === "none")
    return set(
      groupLabels.map((l) => l.id),
      true,
    );
  if (v === "*")
    return set(
      groupLabels.map((l) => l.id),
      false,
    );
  const ids = groupLabels.filter((l) => lower(l.slug) === v || nameMatches(value, l.name)).map((l) => l.id);
  return orNull(ids, (found) => set(found, false));
}

/** Resolves sort keys; `sort:<group>` needs a known single-select label group. */
export function resolveSort(
  sorts: readonly QuerySort[],
  ctx: ResolveContext,
  warnings: QueryWarning[],
): ResolvedSort[] {
  const out: ResolvedSort[] = [];
  for (const sort of sorts) {
    if (sort.key !== "group") {
      out.push(sort);
      continue;
    }
    const slug = sort.group ?? "";
    const groups = groupsBySlug(ctx, slug);
    const raw = `sort:${slug}`;
    if (groups.length === 0) {
      warnings.push({ code: "invalid_value", raw, field: "sort", value: slug });
    } else if (groups.some((g) => !g.exclusive)) {
      // Items carry several labels of a multi-select Field, so there is no single position to order by.
      warnings.push({ code: "unsortable_field", raw, field: "sort", value: slug });
    } else {
      const ids = new Set(groups.map((g) => g.id));
      const positions: Record<string, number> = {};
      for (const l of ctx.labels) if (l.groupId && ids.has(l.groupId)) positions[l.id] = l.position;
      out.push({ ...sort, groupIds: [...ids], positions });
    }
  }
  return out;
}

function orNull<T>(ids: T[], build: (ids: T[]) => Condition): Condition | null {
  return ids.length > 0 ? build(ids) : null;
}

export function resolveQuery(ast: QueryAst, ctx: ResolveContext): ResolvedQuery {
  const conditions: Condition[] = [];
  const warnings: QueryWarning[] = [...ast.warnings];
  let archived: ResolvedQuery["archived"] = "exclude";

  for (const clause of ast.clauses) {
    if (clause.kind === "text") {
      const groups = clause.fieldRef ? groupsBySlug(ctx, clause.fieldRef.key) : [];
      if (!clause.fieldRef || groups.length === 0) {
        conditions.push({ t: "text", text: clause.text, negate: clause.negate });
        continue;
      }
      // A label-group slug used as a field key: the parser's unknown_field warning does not apply.
      const stale = warnings.findIndex((w) => w.code === "unknown_field" && w.raw === clause.raw);
      if (stale >= 0) warnings.splice(stale, 1);
      const parts: Condition[] = [];
      for (const value of clause.fieldRef.values) {
        const c = resolveFieldValue(ctx, groups, value);
        if (c) parts.push(c);
        else {
          warnings.push({ code: "unknown_value", raw: clause.raw, field: clause.fieldRef.key, value });
          parts.push(NOTHING);
        }
      }
      const combined: Condition = parts.length === 1 ? parts[0]! : { t: "or", conditions: parts, negate: false };
      conditions.push(clause.negate ? negated(combined) : combined);
      continue;
    }
    if (clause.field === "is" && clause.values.length === 1 && lower(clause.values[0]!) === "archived") {
      archived = clause.negate ? "exclude" : "only";
      continue;
    }
    const parts: Condition[] = [];
    for (const value of clause.values) {
      const c = resolveValue(ctx, clause, value);
      if (c) parts.push(c);
      else {
        const code =
          clause.field === "is" || clause.field === "has" || COMPARABLE_FIELDS.has(clause.field)
            ? "invalid_value"
            : "unknown_value";
        warnings.push({ code, raw: clause.raw, field: clause.field, value });
        parts.push(NOTHING);
      }
    }
    const combined: Condition = parts.length === 1 ? parts[0]! : { t: "or", conditions: parts, negate: false };
    conditions.push(clause.negate ? negated(combined) : combined);
  }

  return { conditions, archived, sort: resolveSort(ast.sort, ctx, warnings), warnings };
}
